import React from 'react';
import Switcher from '../../../../../../components/common/switcher/Switcher';
import { Section } from '../../../../../../models/productDivision/types';
import { BrandCompany, GroupedProduct, Product } from '../../../../../../models/product/types';
import { ProductReferences } from '../../../../../../redux/actions/productActions/types';
import { blueDark01 } from '../../../../../../styles/colors';
import { Text } from '../../../../../../styles/common.styles';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import ProductItem from '../guidelineProductItem/GuidelineProductItem';
import {
    MFRTitleContainer,
    ProductsContainer,
    ProductScrollableContainer,
    SectionItemContainer,
    SectionItemDivider,
    SectionItemTitleContainer,
    SectionItemWrapper,
    SectionTitle,
    ProductCheckbox,
} from '../../../../../projectInfo/tabs/productsTab/sectionItem/SectionItem.styles';

interface Props {
    liveGuidelineId: number;
    section: Section<Product>;
    references: ProductReferences | null;
    canHideProducts: boolean;
    editable: boolean;
    brandsInfo: Array<BrandCompany>;
    lastSection: boolean;
    filterModalOpened?: boolean;
    selectedProducts?: Product[];
    handleClicked: (id: string) => void;
    updateDivisions: (product: Product, isInstalled?: boolean) => void;
    handleDeleteProduct: (product: Product) => void;
    handleSelectProduct: (event: CheckboxChangeEvent, product: Product) => void;
}

const GuidelineSectionItem: React.FC<Props> = ({
    liveGuidelineId,
    section,
    references,
    canHideProducts,
    editable,
    brandsInfo,
    lastSection,
    updateDivisions,
    handleClicked,
    handleDeleteProduct,
    filterModalOpened,
    handleSelectProduct,
    selectedProducts,
}) => {
    let masterFormatProducts = Array<GroupedProduct<Product>>();
    for (const product of section.products) {
        product.lowestLevelNumber =
            product.mfSection?.number ||
            product?.mfSectionLevel3?.number ||
            product?.mfSectionLevel2?.number ||
            product?.mfSectionLevel1?.number ||
            '';
        product.lowestLevelLabel =
            product.mfSection?.label ||
            product.mfSectionLevel3?.label ||
            product.mfSectionLevel2?.label ||
            product.mfSectionLevel1?.label ||
            '';
    }
    const groupedMap = section.products.reduce(
        (entryMap, e) =>
            entryMap.set(e.lowestLevelNumber as any, [
                ...(entryMap.get(e.lowestLevelNumber as any) || []),
                e,
            ]),
        new Map<string | undefined, Array<Product>>(),
    );
    for (const [key, value] of groupedMap.entries()) {
        masterFormatProducts.push({
            products: value,
            number: key,
            label: value[0].lowestLevelLabel,
        });
    }
    masterFormatProducts = sortByKey(masterFormatProducts, 'number');
    for (const mfProducts of masterFormatProducts) {
        mfProducts.products = sortByKey(mfProducts.products, 'productName');
        mfProducts.products = sortByKey(mfProducts.products, 'type');
    }

    function sortByKey(array, key) {
        return array.sort(function (a, b) {
            var x = a[key];
            var y = b[key];

            if (typeof x == 'string') {
                x = ('' + x).toLowerCase();
            }
            if (typeof y == 'string') {
                y = ('' + y).toLowerCase();
            }

            let result: number;

            if (x < y) {
                result = -1;
            } else if (x > y) {
                result = 1;
            } else {
                result = 0;
            }

            return result;
        });
    }

    function isProductManuallyAdded(product: Product, brandInfo: BrandCompany | undefined) {
        if (product && brandInfo) {
            return product.type === 'userProducts' && brandInfo?.type === 'companies';
        }

        return false;
    }

    function getBrandInfo(product: Product) {
        return brandsInfo.find((b) => b.id === product.companyID);
    }

    const getManuallyAddedProductsByBrand = (products: Product[], companyName: string) => {
        return products.filter((product: Product) => {
            const brandInfo = getBrandInfo(product);
            return isProductManuallyAdded(product, brandInfo) && product.company === companyName;
        });
    };

    const getDividerValue = (
        nextProduct: Product,
        currentProduct: Product,
        currentProductBrandInfo: BrandCompany | undefined,
    ) => {
        let dividerValue = false;
        if (nextProduct) {
            const nextProductBrandInfo = getBrandInfo(nextProduct);

            // If the next product is from a different brand, set divider to true
            if (currentProduct.company !== nextProduct.company) {
                dividerValue = true;
            }

            // Additional condition: if the next product is a manually added product - show divider
            if (isProductManuallyAdded(nextProduct, nextProductBrandInfo)) {
                dividerValue = true;

                // Exception: if the current and next products are manually added products with the same brand,
                // then don't show the divider
                if (
                    isProductManuallyAdded(currentProduct, currentProductBrandInfo) &&
                    currentProduct.company === nextProduct.company
                ) {
                    dividerValue = false;
                }
            }
        }

        return dividerValue;
    };

    return (
        <>
            <SectionItemDivider />
            <SectionItemWrapper>
                <SectionItemContainer>
                    <SectionItemTitleContainer
                        onClick={() => {
                            handleClicked(section.id);
                        }}
                    >
                        <Switcher
                            id={section.id}
                            clicked={section.clicked}
                            handleSwitch={() => handleClicked(section.id)}
                            width={20}
                            height={20}
                            small
                        />
                        <SectionTitle className="section-id hover">{section.id}</SectionTitle>
                        <SectionTitle className="hover">{section.name}</SectionTitle>{' '}
                        <Text
                            weight={400}
                            size={16}
                            height={20}
                            color={blueDark01}
                            className="hover"
                        >
                            ({section.products.length})
                        </Text>
                    </SectionItemTitleContainer>
                </SectionItemContainer>
            </SectionItemWrapper>
            <ProductsContainer className={section.clicked ? 'clicked' : ''}>
                {masterFormatProducts.map((group, j) => (
                    <ProductScrollableContainer key={j}>
                        <SectionItemDivider />
                        <MFRTitleContainer className={j ? 'sec' : ''}>
                            <SectionTitle className="section-id mfr">{group.number}</SectionTitle>
                            <SectionTitle className="mfr">{`${group.label}`}</SectionTitle>{' '}
                            <Text
                                weight={400}
                                size={14}
                                height={18}
                                color={blueDark01}
                                className="hover"
                            >
                                ({group.products.length})
                            </Text>
                        </MFRTitleContainer>
                        <SectionItemDivider />
                        {sortByKey(group.products, 'company').map(
                            (product: Product, i: number, products: Product[]) => {
                                if (!brandsInfo) {
                                    return <></>;
                                }

                                let divider = false;
                                let previousProductManuallyAdded = false;
                                let previousProduct = undefined;
                                let previousProductBrandInfo = undefined;
                                let manuallyAddedProductsByBrand = undefined;

                                const nextProduct = products[i + 1];
                                const currentProductBrandInfo = getBrandInfo(product);

                                if (i - 1 >= 0) {
                                    previousProduct = products[i - 1];
                                    previousProductBrandInfo = getBrandInfo(previousProduct);
                                    if (previousProductBrandInfo) {
                                        previousProductManuallyAdded = isProductManuallyAdded(
                                            previousProduct,
                                            previousProductBrandInfo,
                                        );
                                    }
                                }

                                if (
                                    !previousProductManuallyAdded &&
                                    isProductManuallyAdded(product, currentProductBrandInfo)
                                ) {
                                    manuallyAddedProductsByBrand = getManuallyAddedProductsByBrand(
                                        products,
                                        product.company,
                                    );
                                }

                                divider = getDividerValue(
                                    nextProduct,
                                    product,
                                    currentProductBrandInfo,
                                );

                                return (
                                    <>
                                        <ProductCheckbox
                                            key={section.id}
                                            checked={selectedProducts?.some(
                                                (p) =>
                                                    (p.projectProductId ?? p.id) ===
                                                    (product.projectProductId ?? product.id),
                                            )}
                                            onChange={(e) => handleSelectProduct(e, product)}
                                        />
                                        <ProductItem
                                            liveGuidelineId={liveGuidelineId}
                                            filterModalOpened={filterModalOpened}
                                            previousProduct={i ? products[i - 1] : undefined}
                                            key={product.projectProductId ?? product.id}
                                            product={product}
                                            references={references}
                                            canHideProducts={canHideProducts}
                                            divider={divider}
                                            updateDivisions={updateDivisions}
                                            handleDeleteProduct={handleDeleteProduct}
                                            editable={editable}
                                            brandInfo={currentProductBrandInfo}
                                            lastProduct={
                                                masterFormatProducts.length === j + 1 &&
                                                group.products.length === i + 1 &&
                                                lastSection
                                            }
                                            previousProductManuallyAdded={
                                                previousProductManuallyAdded
                                            }
                                            manuallyAddedProductsByBrand={
                                                manuallyAddedProductsByBrand
                                            }
                                        />
                                    </>
                                );
                            },
                        )}
                    </ProductScrollableContainer>
                ))}
            </ProductsContainer>
        </>
    );
};

export default GuidelineSectionItem;
